import { useCallback } from 'react';

import { fetchRetailerApiSettings } from '~/api/easy-estimator-api/lookup-retailer-api/lookupRetailerApiService';
import { BEST_SELLER_FLAG, BEST_SELLING_FLAG } from '~/core/constants';
import { SortBy } from '~/core/enums';
import i18n from '~/core/i18n/i18n';
import { TypeOfFilter } from '~/core/types/sortAndFilter';

const TRENDING_NOW_KEY = 'SCROLLABLE_AREA.COLOR.TRENDING_NOW';
const BEST_SELLING_KEY = 'SCROLLABLE_AREA.BESTSELLING';
const SAGE_RETAILER_CODE = 'sage';

/*
  This hook is used to get the trending now label and the sorted by value for the sinks tab.
*/
export const useTrendingLabels = (filters?: TypeOfFilter[]) => {
  const getSageRetailer = useCallback(async () => {
    const { apiHostname } = await fetchRetailerApiSettings();

    return apiHostname.includes(SAGE_RETAILER_CODE);
  }, []);

  const getTrendingNowLabel = useCallback(async (): Promise<string> => {
    const isSageRetailer = await getSageRetailer();

    if (!filters) {
      return isSageRetailer ? BEST_SELLING_KEY : TRENDING_NOW_KEY;
    }

    const hasBestSellingFilter = filters.some(
      ([categoryTitle]) =>
        categoryTitle.toLowerCase() === BEST_SELLING_FLAG.toLowerCase() ||
        categoryTitle.toLowerCase() === BEST_SELLER_FLAG.toLowerCase()
    );

    return hasBestSellingFilter || isSageRetailer
      ? i18n.t(BEST_SELLING_KEY)
      : i18n.t(TRENDING_NOW_KEY);
  }, [filters, getSageRetailer]);

  const getSortedByValue = useCallback(
    async (sortBy: SortBy): Promise<string> => {
      const isSageRetailer = await getSageRetailer();

      if (sortBy === SortBy.TrendingNow && isSageRetailer) {
        return i18n.t(BEST_SELLING_KEY);
      }

      return i18n.t(sortBy);
    },
    [getSageRetailer]
  );

  return { getTrendingNowLabel, getSortedByValue };
};
